// ============================================================
// BattlefieldTokens - who is standing where.
//
// Every token draws three parts, and each one earns its place:
//
//   FOOTPRINT  an ellipse on the ground: the only honest cue to where a
//              raised token actually IS, since a projected view puts a
//              man on a gantry somewhere he is not standing.
//   STALK      a line from the footprint to the body. Its length IS the
//              elevation - the one thing an orthographic view cannot
//              show any other way.
//   BODY       the marker itself.
//
// ALL THREE ARE ONE THING TO GRAB (v7.25.0). They were two: the feet
// moved you across the ground and the head changed your height, which
// made the smaller target the one you needed most and fired an invisible
// height change every time you missed it. Height is the wheel now.
//
// COLOUR IS TWO CHANNELS. The BODY wears the team's livery - a fact about
// the fiction, the same on every client (v8.313.0; before that the body
// took the role colour and "everyone else" was one orange). The ROLE is
// local - `actorId` and `targetId` are per-browser by design - and it
// paints the footprint stroke and a MARK: gold, with a chevron over the
// head, for the combatant you are acting as (gold is "me" everywhere else
// in the app; the map alone used to say blue), and the auspex red with a
// reticle for the one you are pointing a gun at. Everyone else's stroke
// is their livery again, so a bystander's footprint says nothing more
// than their body does.
// ============================================================
import { memo, useId } from 'react';
import type { Camera } from '../engine/battlefield/projection';
import { project, SCENE_VIEW } from '../engine/battlefield/projection';
import type { Token } from '../engine/battlefield/tokens';
import { tokenFoot } from '../engine/battlefield/tokens';
import { figureFoot, headPoint } from '../engine/display/figures';
import { furrowOf, traitMarks } from '../engine/display/traitMarks';
import type { ScreenTraitMarks } from './battlefieldTokens/TraitMarks';
import { hordeOffsetMs, placeWalking, walkAt, type Walk } from '../engine/display/walk';
import { CROWD } from '../engine/battlefield/tokenShape';
import type { FigureModel } from '../engine/display/figureModel';
import type { MapLook } from '../engine/display/looks';
import type { FigureStyle } from './display/figureStyle';
import { PILL_H, pillWidth, stackPills, type PlacedPill } from '../engine/display/pills';
import { figureShape, figureWorld } from './display/figureShape';
import type { Livery } from '../engine/roster/team';
import type { Impact } from '../engine/impacts';
import type { ShapeKind } from '../engine/battlefield/tokenShape';
import type { MarkerStatus } from '../engine/battlefield/markerStatus';
import type { MapState } from '../engine/display/states';
import type { Fall } from '../hooks/useDisplayBeats';
import { facingRing } from '../engine/display/facingRing';
import type { HordeCount } from './battlefieldTokens/HordeBodies';
import type { ArcPreset } from '../engine/battlefield/facing';
import type { VehicleFacingChoice } from '../engine/vehicleFacing';
import type { ScreenFacingRing } from './battlefieldTokens/TokenFacingRing';
import { useWoundDisplay } from '../hooks/useWoundDisplay';
import { liveryFill, tokenRole, tokenStroke, type TokenRole } from './battlefieldTokens/roles';
import { hoverCardOf, markerGeometry, markerTitle } from './battlefieldTokens/markerGeometry';
import HoverCard from './battlefieldTokens/HoverCard';
import { groundDistance } from '../engine/battlefield/measure';
import type { MarkerLookups, ScreenXY } from './battlefieldTokens/markerGeometry';
import TokenMarker from './battlefieldTokens/TokenMarker';
import { draggingCamera } from '../utils/cameraDrag';
import { markerFrame, screensIn, type MarkerFrame } from './battlefieldTokens/markerFrame';
import type { ProjectedScreen } from '../engine/display/coverScreens';
import displayStyles from '../styles/Display.module.css';
import type { TokenMarkerProps } from './battlefieldTokens/TokenMarker';
import type { ObjectHealth } from './battlefieldTokens/ObjectRing';

export { ROLE_STROKES, tokenRole, tokenStroke } from './battlefieldTokens/roles';

interface BattlefieldTokensProps {
  /** The take-cover cue's screens per figure, projected for this camera (useCoverCue), and what makes them rise. */
  coverScreens?: Record<string, ProjectedScreen[]>;
  coverRiseKeys?: Record<string, string>;
  tokens: Token[];
  camera: Camera;
  actorId?: string | null;
  targetId?: string | null;
  names?: Record<string, string>;
  /** The livery each combatant wears, by id; absent = no team. */
  liveries?: Record<string, Livery | undefined>;
  /** Who is in Squad Mode: the name pill wears the linked chevrons (2026-10-23). */
  inSquad?: ReadonlySet<string>;
  /** The combatant under the pointer, here or on the order rail. */
  hoveredId?: string | null;
  onHoverToken?: (id: string | null) => void;
  /** The actor's reach to each token: a band letter, or BLOCKED (v8.323.0). */
  reach?: Record<string, { band: string | null; blocked: boolean; cover?: string } | undefined>;
  /** The newest impact per combatant, for the marker to wear a moment. */
  impacts?: Record<string, Impact | undefined>;
  /** Tokens are only draggable in Play mode, and only if you may move them. */
  draggable?: boolean;
  /**
   * Whether the browser may hit-test a marker at all.
   *
   * NOT THE SAME QUESTION AS DRAGGABLE, which is what this was reading
   * before. Deploy mode turns dragging off on purpose - a marker must
   * never swallow the click that plants the flag - and that also made
   * the whole group `pointer-events: none`, so right-click-to-recall
   * could not hit anybody and the feature was unreachable from the
   * moment it shipped (v8.22.0).
   *
   * Build keeps them inert: there, a click that lands on a marker
   * instead of the wall behind it would select the wrong thing.
   */
  pickable?: boolean;
  /** Names beside the markers, for orienting at a glance. */
  showLabels?: boolean;
  /**
   * What kind of thing each marker is, keyed by token id.
   *
   * Derived by the panel each frame from the live projection, never
   * stored on the Token: body type is editable while the map is open,
   * and a shape written at deploy would go stale (v8.39.0).
   */
  bodyTypes?: Record<string, ShapeKind>;
  /**
   * A drawn-size override per marker, as a scale on the render radius -
   * the "Token size (m)" field, resolved by the panel (bodyTypes
   * pattern). Visual only: token.footprint stays the truth for blast
   * inclusion, horde radii and heading geometry.
   */
  scales?: Record<string, number>;
  /**
   * Which markers are crowds rather than individuals, keyed by token id.
   *
   * SEPARATE FROM THE SHAPE on purpose. "How many of them" and "what
   * kind of thing" are different questions, so a horde of vehicles is
   * three hulls - folding them into one enum made that inexpressible
   * (v8.43.0).
   */
  crowds?: Record<string, boolean>;
  /**
   * Which way each vehicle's hull points, keyed by token id - degrees,
   * 0 due east. Read straight off the token; only vehicles have one.
   */
  headings?: Record<string, number>;
  /** Whether the heading handle may be grabbed. A WRITE, gated on editTarget. */
  canTurn?: boolean;
  onHeadingPointerDown?: (tokenId: string, e: React.PointerEvent) => void;
  /**
   * What each marker is standing inside, keyed by token id.
   *
   * Normally empty: a move follows the walkable route and stops. It fills
   * up after terrain has been switched off and someone has been put
   * somewhere they could not have walked - they stay there, and this is
   * what says so.
   */
  overlaps?: Record<string, string>;
  /**
   * What each marker says about the combatant standing there, keyed by
   * token id.
   *
   * Already levelled by the panel: an absent entry is a marker with
   * nothing to say, and the `off` setting simply hands over an empty
   * record, so nothing here has to know about preferences.
   */
  status?: Record<string, MarkerStatus>;
  onTokenPointerDown?: (tokenId: string, e: React.PointerEvent) => void;
  /**
   * On the display (plan map-display), how its figures are drawn: the stalk
   * always, the head at half the height, its classes. Absent: the classic
   * marker. The same parts and test ids serve both renderers.
   */
  figure?: FigureStyle;
  /** On the display: who stands behind translucent terrain alone, half seen (T3: useDisplayOcclusion on the veils). */
  halfSeen?: Record<string, boolean>;
  /** On the display: who stands behind terrain, drawn see-through. By token id. */
  occluded?: Record<string, boolean>;
  /** The GM's hidden figures: drawn dashed, the players' screens never hold them (plan map-display, G2). */
  hidden?: ReadonlySet<string>;
  /**
   * On the display: each walk playing, at the clock's time it is drawn at (plan design-gaps, M3). The token list
   * already places the walkers this frame; the marker wears `data-walking`, and a crowd's bodies trail it (M4).
   */
  walks?: Record<string, { walk: Walk; t: number } | undefined>;
  /** On the display: each figure's standing states (plan design-gaps, S2), by token id. */
  states?: Readonly<Record<string, readonly MapState[]>>;
  /** On the display: the states that have just begun, by token id (useOnsets). */
  stateOnsets?: Readonly<Record<string, readonly string[]>>;
  /** On the display: what each figure's posture has just become (S4: hooks/useOnsets), by token id. */
  postureOnsets?: Readonly<Record<string, readonly string[]>>;
  /** On the display: which way each figure fell, as this screen saw the killing blow (S4: useFalls), by token id. */
  falls?: Readonly<Record<string, Fall>>;
  /** On the display: each horde's magnitude and whole, for a body per point (V3: `hordesFor`), by token id. */
  hordes?: Readonly<Record<string, HordeCount>>;
  /** On the display, each Object's health (2026-10-26): its ring is its gauge. */
  objects?: Readonly<Record<string, ObjectHealth>>;
  /** This screen plays no motion (useHoldsStill, read once by the panel): no pops, no drift. */
  still?: boolean;
  /** On the display: what each vehicle's facing ring reads (V1: markerDecorations `vehicleFacingsFor`), by token id. */
  vehicleFacings?: Readonly<Record<string, VehicleFacings>>;
  /** On the display: who a beat shoves this frame (the rush, the parry's throw-back, the throw - K3). */
  shoves?: Readonly<Record<string, unknown>>;
  /** On the display's quiet map: who the action is about; everyone else is drawn dim and unnamed. Null: all. */
  focus?: ReadonlySet<string> | null;
  /**
   * On the display: what each figure's trait reads (plan design-gaps, T1: markerDecorations `traitGroundsFor`) - the
   * height of what it stands on, and whether a ghost is inside a wall - by token id; only figures with either.
   */
  traitGrounds?: Readonly<Record<string, { under?: number; inside?: boolean }>>;
  /** On the display: who stands in the dark, drawn dim (Z2: markerDecorations `inTheDarkFor`), by token id. */
  dark?: Readonly<Record<string, boolean>>;
  /** On the display, in Stealth: each roused watcher's word (Z2: INVESTIGATING, SEARCHING), by token id. */
  alerts?: Readonly<Record<string, string>>;
  /** On the display: the other side, a glow under each (T4: markerDecorations `otherSideOf`), by token id. */
  heat?: Readonly<Record<string, boolean>>;
  /** On the display: the figures just stepping into a surface, with its colour (T3: the entry beat), by token id. */
  surfaceEntries?: Readonly<Record<string, { label: string; colour: string | null }>>;
  /** On the display: the ghosts that have just gone into a wall or come out (useOnsets), by token id. */
  rippleOnsets?: Readonly<Record<string, readonly string[]>>;
  /** On the display: names as pills, stacked clear of each other (plan map-display, C). */
  pills?: boolean;
  /** On the display: no wound numbers on the figures (the readout under the pointer keeps them). */
  numbersOff?: boolean;
  /** On the display: what each combatant is drawn as - class, posture, trait (plan map-display, B). By token id. */
  models?: Record<string, FigureModel>;
  /** On the display: each one's looks (plan body-map M1), by token id; only bodies with any. */
  looks?: Record<string, MapLook[]>;
}

/** Where the body is drawn: the display figure's head (by its model, when known), or the classic marker's. */
function bodyPoint(token: Token, figure: FigureStyle | undefined, model: FigureModel | undefined) {
  if (figure && model) return figureWorld(token, model).head;
  return figure ? headPoint(token) : { x: token.x, y: token.elevation + token.eyeHeight * 0.65, z: token.z };
}

/**
 * Every marker on the map, furthest first - same as the solids, so a token
 * in front paints over one behind it.
 *
 * HOVER is read at the group: the marker's parts opt in to pointer events
 * one by one, so the id is read off whichever part was entered.
 */
function BattlefieldTokensImpl(props: BattlefieldTokensProps) {
  const { tokens, camera, actorId = null, targetId = null, onHoverToken } = props;
  const { showNumbers } = useWoundDisplay();
  const placed = tokens
    .map((token) => {
      const fall = fallOf(token, props);
      // The fall feeds the figure's lying shape alone; the marker's ring, handle and tags keep the stored token.
      const lying = fall ? lyingAlong(token, fall.heading) : token;
      const model = props.figure ? props.models?.[token.id] : undefined;
      // A display figure's ring is where its model stands it: a hoverer's just off the ground (T1).
      const foot = project(model ? figureFoot(token, model) : tokenFoot(token), camera, SCENE_VIEW);
      const body = project(bodyPoint(lying, props.figure, model), camera, SCENE_VIEW);
      return { token, lying, foot, body, role: tokenRole(token.id, actorId, targetId), fell: !!fall };
    })
    // Under perspective, a figure standing behind the eye is not on the screen at all.
    .filter((one) => !one.foot.behind)
    .sort((a, b) => b.foot.depth - a.foot.depth);
  const pills = pillsFor(placed, props);
  const actorFoot = footOfActor(placed);
  const heatId = useId();
  return (
    <g data-testid="battlefield-tokens" {...hoverHandlers(onHoverToken)}>
      {/* The other side's heat (T4): one gradient for every glow on the layer. */}
      <HeatGradient id={heatId} props={props} />
      {placed.map((one) => (
        <TokenMarker
          key={one.token.id}
          {...markerProps(one, props, showNumbers, {
            pill: pills?.[one.token.id],
            coverFrom: coverFromFor(one, actorFoot),
            heat: heatFor(one, props, heatId),
          })}
        />
      ))}
      {/* Over every figure: the hovered one's card (T3), on the display. */}
      {hoverCardFor(placed, props, showNumbers)}
    </g>
  );
}

/** The figure an element belongs to, or null. */
const figureOf = (el: EventTarget | null) =>
  (el as HTMLElement | null)?.closest?.('[data-token-id]')?.getAttribute('data-token-id') ?? null;

/**
 * HOVER is read at the group: the figure entered, and null on leaving every figure. During a camera drag figures
 * slide under a still pointer: the hover is let go instead of chasing them (utils/cameraDrag).
 */
function hoverHandlers(onHoverToken: ((id: string | null) => void) | undefined) {
  return {
    onPointerOver: (e: React.PointerEvent) => onHoverToken?.(draggingCamera(e) ? null : figureOf(e.target)),
    onPointerOut: (e: React.PointerEvent) => {
      if (draggingCamera(e) || !figureOf(e.relatedTarget)) onHoverToken?.(null);
    },
  };
}

/** The heat's glow (T4): the beat's red fading out from the middle, shared by every figure that wears it; none unworn. */
function HeatGradient({ id, props }: { id: string; props: BattlefieldTokensProps }) {
  if (!props.figure || !props.heat || !Object.keys(props.heat).length) return null;
  return (
    <defs>
      <radialGradient id={id}>
        <stop offset="0" className={displayStyles.heatStop} stopOpacity={0.35} />
        <stop offset="1" className={displayStyles.heatStop} stopOpacity={0} />
      </radialGradient>
    </defs>
  );
}

/** Behind translucent terrain alone (T3): opaque cover wins - a figure behind both is hidden, not half seen. */
const halfSeenOf = (id: string, props: BattlefieldTokensProps) => !!props.halfSeen?.[id] && !props.occluded?.[id];

/** The other side's glow (T4) on the display, filled from the layer's gradient; none for anyone else. */
const heatFor = (one: Placed, props: BattlefieldTokensProps, gradient: string) =>
  props.figure && props.heat?.[one.token.id] ? `url(#${gradient})` : undefined;

/** Where the actor stands on the screen, for the target's cover arc toward them (T6); none with no actor placed. */
const footOfActor = (placed: readonly Placed[]) => placed.find((p) => p.role === 'actor')?.foot;

/** The target's cover arc points at the actor's feet; nobody else's. */
const coverFromFor = (one: Placed, actorFoot: ScreenXY | undefined) => (one.role === 'target' ? actorFoot : undefined);

/**
 * The hovered figure's card (T3: `hoverCardOf`), beside its head: its name, wounds and conditions as its tooltip may
 * tell them, and how far it stands from whoever acts. None with nobody hovered, nor over the actor's own figure's
 * distance (it has none from itself).
 */
function hoverCardFor(placed: readonly Placed[], props: BattlefieldTokensProps, showNumbers: boolean) {
  // The display's alone: the classic map keeps its tooltip.
  const one = props.figure && props.hoveredId ? placed.find((p) => p.token.id === props.hoveredId) : undefined;
  if (!one) return null;
  const id = one.token.id;
  const actor = props.actorId && props.actorId !== id ? placed.find((p) => p.token.id === props.actorId) : undefined;
  const from = actor
    ? {
        name: props.names?.[actor.token.id] ?? actor.token.id,
        metres: groundDistance(tokenFoot(actor.token), tokenFoot(one.token)),
      }
    : null;
  const card = hoverCardOf(props.names?.[id] ?? id, props.status?.[id], showNumbers, from);
  return <HoverCard id={id} {...card} head={one.body} stripe={liveryFill(props.liveries?.[id])} />;
}

/**
 * A marker placed on the screen. `lying` is the token its figure is shaped from: the stored one, or - when it lies
 * along the fall this screen saw (`fell`, S4) - a copy turned to that fall.
 */
type Placed = { token: Token; lying: Token; foot: ScreenXY; body: ScreenXY; role: TokenRole; fell: boolean };

/**
 * One turned copy per stored token, kept while the fall's heading holds: the figure's geometry is cached on the
 * token object (`figureWorld`), and a fresh copy every render would miss it on every frame (the Track S simplify).
 */
const LYING = new WeakMap<Token, Token>();
function lyingAlong(token: Token, heading: number): Token {
  const kept = LYING.get(token);
  if (kept?.heading === heading) return kept;
  const turned = { ...token, heading };
  LYING.set(token, turned);
  return turned;
}

/**
 * The fall a dead figure lies along: the way its killing blow threw it, away from the attacker, when this screen saw
 * the blow (plan design-gaps, S4; `useFalls`); else none, and it lies along its own heading, as before. A body
 * someone has turned since it fell lies as they turned it: its stored heading is no longer the one it fell with
 * (the Track S review).
 */
function fallOf(token: Token, props: BattlefieldTokensProps): Fall | null {
  const fall = props.falls?.[token.id];
  const dead = props.models?.[token.id]?.posture === 'dead';
  return fall && dead && (fall.was ?? 0) === (token.heading ?? 0) ? fall : null;
}

/** Where each name pill sits, above its head and clear of the others; null when names are not pills. */
function pillsFor(placed: readonly Placed[], props: BattlefieldTokensProps) {
  if (!props.pills || !props.showLabels) return null;
  const named = placed.filter((one) => !props.focus || props.focus.has(one.token.id));
  return stackPills(
    named.map((one) => {
      const w = pillWidth(props.names?.[one.token.id] ?? one.token.id, 0);
      return { id: one.token.id, x: one.body.x - w / 2, y: one.body.y - PILL_LIFT - PILL_H, w };
    }),
  );
}

/** How far above the head point a pill's lower edge sits, in scene units. */
const PILL_LIFT = 14;

/** The per-marker records the geometry reads, each empty when absent. */
function lookupsOf({ bodyTypes, scales, crowds, headings }: BattlefieldTokensProps): MarkerLookups {
  return { bodyTypes: bodyTypes ?? {}, scales: scales ?? {}, crowds: crowds ?? {}, headings: headings ?? {} };
}

/** What the layer works out across markers for one of them, on the map's own screen: placed into its frame here. */
interface Extra {
  pill: PlacedPill | undefined;
  coverFrom: ScreenXY | undefined;
  heat: string | undefined;
}

/** The take-cover cue's screens round a display figure (projected on the map), in its frame. */
function coverScreensIn(id: string, props: BattlefieldTokensProps, frame: MarkerFrame) {
  const screens = props.figure ? props.coverScreens?.[id] : undefined;
  return screens && screensIn(frame, screens);
}

/** A display figure's shape, when this marker is one and its model is known. */
function shapeOf(token: Token, props: BattlefieldTokensProps, frame: MarkerFrame) {
  const model = props.figure && props.models?.[token.id];
  return model ? figureShape(token, model, props.camera, frame.at) : undefined;
}

/** What a vehicle's facing ring reads (V1): its armour by facing, the shooter's place, the table's arc preset. */
interface VehicleFacings {
  armour: { front: number; side: number; rear: number };
  shooter: { x: number; z: number } | null;
  preset: ArcPreset;
  /** The hull's own facing choice: a GM's pin, which the attack meets whatever the map says, or 'auto'. */
  choice?: VehicleFacingChoice | null;
}

/** A vehicle's facing ring round its hull where it is drawn this frame, on the screen; none off the display. */
function facingRingFor(token: Token, props: BattlefieldTokensProps, frame: MarkerFrame): ScreenFacingRing | undefined {
  // The display's alone: the classic map keeps its heading arrow.
  const spec = props.figure ? props.vehicleFacings?.[token.id] : undefined;
  if (!spec) return undefined;
  // At the hull's drawn size: its base ring is its facing ring (a Token size override scales both).
  const hull = { ...token, footprint: token.footprint * (props.scales?.[token.id] ?? 1) };
  const ring = facingRing(hull, spec.armour, spec.shooter, spec.preset, spec.choice);
  if (!ring) return undefined;
  const { at } = frame;
  return {
    arcs: ring.arcs.map((a) => ({ ...a, points: a.points.map(at), label: at(a.label) })),
    bow: ring.bow.map(at),
  };
}

/**
 * What a display figure's trait and size draw round it this frame (T1: engine/display/traitMarks), on the screen; none
 * on the classic map, nor for a figure with nothing to draw.
 */
function traitMarksFor(token: Token, props: BattlefieldTokensProps, frame: MarkerFrame): ScreenTraitMarks | undefined {
  const model = props.figure ? props.models?.[token.id] : undefined;
  if (!model) return undefined;
  const ground = props.traitGrounds?.[token.id];
  const marks = traitMarks(token, model, {
    under: ground?.under ?? token.elevation,
    inside: !!ground?.inside,
    halfSeen: halfSeenOf(token.id, props),
    hidden: !!props.hidden?.has(token.id),
    alert: props.alerts?.[token.id],
  });
  const playing = model.trait === 'burrower' && model.posture === 'stand' ? props.walks?.[token.id] : undefined;
  const furrow = playing ? furrowOf(playing.walk, playing.t) : [];
  const wire = model.trait === 'incorporeal';
  if (!marks.plumb && !marks.drops.length && !furrow.length && !marks.tag && !wire) return undefined;
  const { at } = frame;
  return {
    plumb: marks.plumb && { from: at(marks.plumb.from), to: at(marks.plumb.to) },
    drops: marks.drops.map(([top, bottom]) => [at(top), at(bottom)]),
    furrow: furrow.map((p) => ({ at: at(p.at), age: p.age })),
    tag: marks.tag,
    wire,
    ripple: !!props.rippleOnsets?.[token.id]?.length,
  };
}

/**
 * A walking crowd FLOWS (plan design-gaps, M4): each body a beat behind the one before (`hordeOffsetMs`), as the
 * screen offset of its own point on the route from the lead's. Undefined for an individual, or a crowd standing still.
 */
function crowdShiftFor(one: Placed, props: BattlefieldTokensProps, frame: MarkerFrame): ScreenXY[] | undefined {
  const playing = props.crowds?.[one.token.id] ? props.walks?.[one.token.id] : undefined;
  if (!playing) return undefined;
  // Three waves, each a beat behind the one before: the classic map's three bodies are the three, and the display's
  // horde walks a wave per group (V3: HordeBodies `waveOf`), so a large horde does not trail for seconds.
  return CROWD.map((_, w) => {
    const behind = walkAt(playing.walk, playing.t - hordeOffsetMs(w));
    // Through the lead's frame: an offset from its foot, rounded as every point of the marker is.
    const { x, y } = frame.at(tokenFoot(placeWalking(one.token, behind)));
    return { x, y };
  });
}

/**
 * One marker's props, from its placement and the per-marker records - every screen point in the marker's own frame,
 * its foot the origin (markerFrame: a camera frame then moves the marker, and rewrites next to nothing inside it).
 */
function markerProps(
  placed: Placed,
  props: BattlefieldTokensProps,
  showNumbers: boolean,
  extra: Extra,
): TokenMarkerProps {
  const { lying: _shapedFrom, ...one } = placed;
  const id = one.token.id;
  const livery = props.liveries?.[id];
  const name = props.names?.[id];
  const status = props.status?.[id];
  const overlap = props.overlaps?.[id];
  const frame = markerFrame(one.foot, props.camera);
  const foot = frame.local(one.foot);
  const body = frame.local(one.body);
  return {
    ...one,
    place: frame.origin,
    foot,
    body,
    g: markerGeometry(one.token, foot, body, props.camera, lookupsOf(props), frame.at),
    livery,
    colour: tokenStroke(one.role, livery),
    fill: liveryFill(livery),
    title: markerTitle(one.token, name, status, showNumbers, overlap),
    name,
    inSquad: !!props.inSquad?.has(id),
    hovered: props.hoveredId === id,
    draggable: props.draggable ?? false,
    pickable: props.pickable ?? false,
    canTurn: props.canTurn ?? false,
    showLabels: props.showLabels ?? false,
    showNumbers: showNumbers && !props.numbersOff,
    status,
    impact: props.impacts?.[id],
    reach: props.reach?.[id],
    overlap,
    figure: props.figure,
    shape: shapeOf(placed.lying, props, frame),
    looks: props.figure ? props.looks?.[id] : undefined,
    occluded: !!props.occluded?.[id],
    halfSeen: halfSeenOf(id, props),
    dark: !!props.figure && !!props.dark?.[id],
    hidden: !!props.hidden?.has(id),
    walking: !!props.walks?.[id],
    shoved: !!props.shoves?.[id],
    states: props.states?.[id],
    stateOnsets: props.stateOnsets?.[id],
    postureOnset: props.postureOnsets?.[id]?.[0],
    crowdShift: crowdShiftFor(placed, props, frame),
    facingRing: facingRingFor(one.token, props, frame),
    coverScreens: coverScreensIn(id, props, frame),
    coverRiseKey: props.coverRiseKeys?.[id],
    traits: traitMarksFor(one.token, props, frame),
    pill: extra.pill && frame.local(extra.pill),
    coverFrom: extra.coverFrom && frame.local(extra.coverFrom),
    heat: extra.heat,
    entering: props.figure ? props.surfaceEntries?.[id] : undefined,
    horde: props.figure ? props.hordes?.[id] : undefined,
    object: props.figure ? props.objects?.[id] : undefined,
    still: !!props.still,
    quiet: !!props.focus && !props.focus.has(id),
    onTokenPointerDown: props.onTokenPointerDown,
    onHeadingPointerDown: props.onHeadingPointerDown,
  };
}

export default memo(BattlefieldTokensImpl);
